/**
 * UltrasoundSection Component
 * 
 * Container component that integrates ultrasound trend chart with baseline management
 * components in a horizontal Row layout. This creates a comprehensive ultrasound 
 * monitoring section where users can view both trend data and baseline management
 * in a unified interface.
 * 
 * This component integrates with the equipment context to pass site, asset, and position
 * props to child components. Individual child components handle their own data fetching
 * to avoid duplicate API calls.
 */

import React, { useCallback, useState, useEffect } from 'react';
import Heading from '@amzn/meridian/heading';
import Card from '@amzn/meridian/card';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import { BaselineRecommendationCard } from './baseline-recommendation-card';
import { BaselineDataCard } from './baseline-data-card';
import { SoundDataCard } from './sound-data-card';
import { CurrentStatusCard } from './current-status-card';
import { HealthyStatusCard } from './healthy-status-card';
import { PulseChart } from '../trend-charts/technology-chart';
import { UltrasoundErrorBoundary } from './ultrasound-error-boundary';
import { useEquipmentContext } from '../../../contexts/equipment-context';
import { useUltsSoundFiles } from '../../../hooks/use-ults-sound-files';

export interface UltrasoundSectionProps {
  /** Chart data for the pulse chart */
  data?: any;
  /** Loading state for the pulse chart */
  isLoading?: boolean;
  /** Error state for the pulse chart */
  error?: string | null;
}

/**
 * UltrasoundSection container component that uses Meridian Row component
 * with ultrasound UI components as children. This component creates a 50/50
 * column split between trend chart and baseline components.
 * 
 * Integrates with equipment context to get site, asset, and position parameters
 * and passes them to child components. Child components handle their own data 
 * fetching to prevent duplicate API calls.
 */
export const UltrasoundSection: React.FC<UltrasoundSectionProps> = ({
  data,
  isLoading,
  error,
}) => {
  // Get equipment context state
  const { state } = useEquipmentContext();
  
  // Extract parameters from equipment context only
  const site = state.site || '';
  const asset = state.apmId || '';
  const position = state.position || '';
  // const position = (state.selectedNode?.type === 'position' ? state.selectedNode.id : '');
  
  // Load sound files for auto-selection
  const { data: soundFiles } = useUltsSoundFiles({
    site,
    asset,
    position
  });
  
  // State for selected date from chart clicks 
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  
  // Auto-select first sound file when data loads (only if user hasn't manually selected)
  useEffect(() => {
    if (soundFiles?.soundFiles && soundFiles.soundFiles.length > 0 && !selectedDate && !hasAutoSelected) {
      const firstSoundFile = soundFiles.soundFiles[0];
      console.log('Auto-selecting first sound file:', firstSoundFile.date);
      setSelectedDate(firstSoundFile.date);
      setHasAutoSelected(true);
    }
  }, [soundFiles, selectedDate, hasAutoSelected]);
  
  // Reset auto-selection flag when equipment context changes
  useEffect(() => {
    setHasAutoSelected(false);
    setSelectedDate(undefined);
  }, [site, asset, position]);
  
  // Set default container width and calculate chart width for left column (50% of container width)
  const containerWidth = 1200;
  const chartWidth = Math.floor(containerWidth / 2);

  // Handle chart data point clicks
  const handleChartClick = useCallback((datum: any) => {
    console.log('PULSE chart clicked:', datum);
    // Convert datum.x to YYYY-MM-DD format for exact date matching
    const clickedDate = new Date(datum.x).toISOString().split('T')[0];
    console.log('Selected date:', clickedDate);
    setSelectedDate(clickedDate);
  }, []);

  // Error boundary error handler
  const handleUltrasoundError = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Ultrasound section error:', error, errorInfo);
    // Could also send to error reporting service here
  }, []);

  return (
    <UltrasoundErrorBoundary onError={handleUltrasoundError}>
      <Column spacing="400">
        <Heading level={4}>PULSE (ULTS)</Heading>
        
        {/* 2x2 Grid Layout */}
        <Row spacing="400" alignmentVertical="stretch">
          <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '24px'}}>
            
            <UltrasoundErrorBoundary onError={handleUltrasoundError}>
              <BaselineRecommendationCard 
                site={site}
                asset={asset}
                position={position}
              />
            </UltrasoundErrorBoundary>
            <UltrasoundErrorBoundary onError={handleUltrasoundError}>
              <Card>
                <PulseChart 
                  data={data}
                  isLoading={isLoading}
                  error={error}
                  containerWidth={chartWidth}
                  onDataPointClick={handleChartClick}
                  enableDataPointClicks={true}
                  pulseColors={{
                    dB: '#2563eb',              
                    baseline: '#16a34a',        
                    lubrication: '#efd807',     
                    startOfFailure: '#ea580c',  
                    criticalFailure: '#ef4444'
                  }}
                />
              </Card>
            </UltrasoundErrorBoundary>
          </div>

          <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '24px'}}>
            
            <UltrasoundErrorBoundary onError={handleUltrasoundError}>
              <CurrentStatusCard 
                site={site}
                asset={asset}
                position={position}
                selectedDate={selectedDate}
              />
            </UltrasoundErrorBoundary>

            <UltrasoundErrorBoundary onError={handleUltrasoundError}>
              <HealthyStatusCard 
                site={site}
                asset={asset}
                position={position}
              />
            </UltrasoundErrorBoundary>
          </div>

        </Row>
      </Column>
    </UltrasoundErrorBoundary>
    
  );
};
